import { Progress, SizableText, XStack, YStack } from '@mythical/ui'
import { ButtonLink } from 'app/components/primitive/Button'
import { useStores } from 'app/store'
import { CollectiblesUpgradePrompt } from './CollectiblesUpgradePrompt'
import { SolitoImage } from 'solito/image'
import { formatDateUS } from 'app/lib/format-date'

export type CollectibleStatus = 'eligible' | 'redeemed' | 'locked'

export type CompactCollectibleProgressProps = {
  title: string
  description: string
  quarter: string
  image: string
  current: number
  total: number
  unlockDate?: string
  status: CollectibleStatus
  isPurchasable?: boolean | null
  isEligibleForCollectibles: boolean
  shopifyUrl?: string
  redeemedDate?: string
  isMonthlyPlan?: boolean
}

export const CompactCollectibleProgress = ({
  title,
  description,
  quarter: _quarter,
  image,
  current,
  total,
  unlockDate,
  status,
  isPurchasable,
  isEligibleForCollectibles,
  shopifyUrl,
  redeemedDate,
  isMonthlyPlan,
}: CompactCollectibleProgressProps) => {
  const { userStore } = useStores()
  const canMemberfulLink = userStore?.canMemberfulLink ?? false
  const safeTotal = Math.max(total, 1)
  const safeCurrent = Math.max(0, Math.min(current, safeTotal))
  const progressPercentage = Math.round((safeCurrent / safeTotal) * 100)
  const remaining = Math.max(safeTotal - safeCurrent, 0)

  const isInProgress =
    !isPurchasable && status === 'locked' && !!isMonthlyPlan && remaining > 0

  if (!isEligibleForCollectibles) {
    return <CollectiblesUpgradePrompt canMemberfulLink={canMemberfulLink} />
  }

  return (
    <YStack bg="$bgTertiary" br="$4" p="$4" w="100%" gap="$3">
      <YStack gap="$1.5">
        <SizableText ff="$heading" size="$s" color="$fgPrimary" tt="uppercase">
          {isInProgress ? 'Next Collectible Progress' : title}
        </SizableText>

        {isInProgress && (
          <SizableText size="$3" color="$fgSecondary">
            You're working towards the {title}
          </SizableText>
        )}
      </YStack>

      <XStack gap="$4" ai="center">
        <YStack w={80} br="$4" ov="hidden" aspectRatio={1}>
          <SolitoImage
            src={image}
            fill
            contentFit="cover"
            alt={title}
            sizes="(min-width: 660px) 277px, 170px"
          />
        </YStack>

        <YStack flex={1} gap="$2">
          {isInProgress && (
            <>
              <XStack jc="space-between" ai="center" gap="$4">
                <SizableText fs={1} ff="$title" size="$m">
                  Progress: {safeCurrent} of {safeTotal} payments
                </SizableText>
                <SizableText ff="$heading" size="$s" color="$brandPurple">
                  {progressPercentage}%
                </SizableText>
              </XStack>

              <Progress
                value={progressPercentage}
                max={100}
                h="$1"
                bg="$bgSecondary"
                br="$4"
                miw="auto"
              >
                <Progress.Indicator
                  animation="quick"
                  bg="$brandPurple"
                  br="$4"
                />
              </Progress>

              <SizableText size="$m" color="$fgSecondary">
                {remaining} more {remaining === 1 ? 'payment' : 'payments'}{' '}
                needed to unlock
              </SizableText>
            </>
          )}

          {status === 'eligible' && shopifyUrl && (
            <YStack gap="$2" jc="flex-start" ai="flex-start">
              <SizableText size="$3" color="$fgSecondary">
                {isPurchasable
                  ? 'You can purchase this collectible now.'
                  : 'You can claim this collectible now.'}
              </SizableText>

              <ButtonLink size="$3" href={shopifyUrl} target="_blank">
                {isPurchasable ? 'Buy Now' : 'Claim Now'}
              </ButtonLink>
            </YStack>
          )}

          {status === 'redeemed' && (
            <SizableText size="$3">
              Claimed{' '}
              {redeemedDate
                ? `on ${formatDateUS(redeemedDate)}`
                : 'successfully'}
            </SizableText>
          )}

          {status === 'locked' && !isInProgress && (
            <SizableText size="$3">
              {unlockDate
                ? `Available on ${formatDateUS(unlockDate)}`
                : description}
            </SizableText>
          )}
        </YStack>
      </XStack>
    </YStack>
  )
}
